import type { PageContext } from '@template-project/funnels-contract';
import { normalizeEventName } from '@trafilea/tss-lib-funnel-schema-ts';
import type { FunnelEventBus } from './funnel-event-bus';
import type { TssEventEnvelope, UIEventPayload } from './envelope';

export interface BehavioralAccumulatorState {
  scrollDepthPct: number;
  timeOnPageSec: number;
  exitIntent: boolean;
}

export function createBehavioralAccumulator(initial?: Partial<BehavioralAccumulatorState>) {
  const state: BehavioralAccumulatorState = {
    scrollDepthPct: initial?.scrollDepthPct ?? 0,
    timeOnPageSec: initial?.timeOnPageSec ?? 0,
    exitIntent: initial?.exitIntent ?? false,
  };
  let stepEnteredAt = Date.now();

  return {
    state,
    resetStepTimer() {
      stepEnteredAt = Date.now();
    },
    foldIntoPageContext(base: Partial<PageContext>): Partial<PageContext> {
      const elapsed = Math.floor((Date.now() - stepEnteredAt) / 1000);
      return {
        ...base,
        scrollDepthPct: Math.max(base.scrollDepthPct ?? 0, state.scrollDepthPct),
        timeOnPageSec: Math.max(base.timeOnPageSec ?? 0, state.timeOnPageSec + elapsed),
        exitIntent: base.exitIntent || state.exitIntent,
      };
    },
  };
}

export function attachBehavioralAccumulator(
  bus: FunnelEventBus,
): { unsubscribe: () => void; accumulator: ReturnType<typeof createBehavioralAccumulator> } {
  const accumulator = createBehavioralAccumulator();

  const sub = bus.on('*', (envelope: TssEventEnvelope) => {
    const payload = envelope.payload as UIEventPayload | undefined;
    const params = payload?.params ?? {};
    if (typeof params.scrollDepth === 'number') {
      accumulator.state.scrollDepthPct = Math.max(
        accumulator.state.scrollDepthPct,
        params.scrollDepth as number,
      );
    }
    if (normalizeEventName(envelope.name) === 'exit_intent') {
      accumulator.state.exitIntent = true;
    }
  });

  return { unsubscribe: sub.unsubscribe, accumulator };
}
